import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Popover,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LuPencil } from 'react-icons/lu';
import { getCandidateSubmissionSettingsTemplate, ICandidate, IJob } from 'wisestep-api';
import {
  WsDialog,
  WsTypoVariants,
  WsTypoColors,
  WsTypography,
  WsButton,
  SendEmailIcon,
  WsColors,
  WsDialogSize,
  WsEditor,
  useWsEditor,
} from 'wisestep-base';

import { replacePlaceHoldersEmailBody } from '../../../components/BulkEmail/helper/apiCalls';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { useWorkspaceContext } from '../../../context';
import { ClientAction, useClientAuthz } from '../../../hooks/useClientAuthz';

import SubmissionPreview from './SubmissionPreview';

interface SubmitOptionsProps {
  onInternalSubmit: (recruiters: string) => void;
  onExternalSubmit: (proceed: boolean) => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  getSubmissionPayload?: () => ICandidate | null;
  onSubmissionRejection?: () => void;
  job?: IJob;
}

const SubmitOptions = ({
  onInternalSubmit,
  onExternalSubmit,
  anchorEl,
  onClose,
  getSubmissionPayload,
  onSubmissionRejection,
  job,
}: SubmitOptionsProps) => {
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [submissionData, setSubmissionData] = useState<ICandidate | null>(null);
  const [openEmailPreview, setOpenEmailPreview] = useState<boolean>(false);
  const editor = useWsEditor('');
  const { watch } = useFormContext();
  const recruiter = watch('recruiters') || {};
  const [isEmailDataLoaded, setIsEmailDataLoaded] = useState<boolean>(true);
  const { id } = useWorkspaceContext();
  const isAuthorizedViewClientListingAll =
    useClientAuthz(ClientAction.VIEW_CLIENT_LISTING_ALL) || false;

  const onInternalSubmitClick = () => {
    setInternalDialogOpen(true);
    onClose();
  };

  const onExternalSubmitClick = () => {
    getSubmissionPayload && setSubmissionData(getSubmissionPayload());
    setExternalDialogOpen(true);
    onClose();
  };
  const getEmailBody = async (candidateInfo: ICandidate) => {
    try {
      const setting = await getCandidateSubmissionSettingsTemplate(
        job?.submissionFormTemplete ?? '',
        id ?? ''
      );
      const { updatedBody } = await replacePlaceHoldersEmailBody(
        setting?.data,
        candidateInfo,
        job as IJob,
        false
      );
      editor?.commands.setContent(updatedBody);
    } catch (error) {
      console.error('Error fetching submission settings:', error);
    }
  };

  const handleEmailPreview = async () => {
    if (getSubmissionPayload) {
      const candidateInfo = getSubmissionPayload();
      setSubmissionData(candidateInfo);
      setOpenEmailPreview(true);
      setIsEmailDataLoaded(false);
      await getEmailBody(candidateInfo as ICandidate);
      setIsEmailDataLoaded(true);
    }
  };

  const handleInternalSubmit = () => {
    if (isEmpty(recruiter)) return;
    onInternalSubmit(recruiter);
    setInternalDialogOpen(false);
  };

  const handleExternalSubmit = (proceed: boolean) => {
    onExternalSubmit(proceed);
    setExternalDialogOpen(false);
  };

  const handleSubmissionRejection = () => {
    onSubmissionRejection && onSubmissionRejection();
    onClose();
  };

  const isPopover = Boolean(anchorEl);

  return (
    <>
      {isPopover && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box p={2} display="flex" flexDirection="column" gap={1}>
            <WsButton variant="text" startIcon={<LuPencil />} onClick={onInternalSubmitClick}>
              Submit Internal
            </WsButton>
            <WsButton variant="text" startIcon={<SendEmailIcon />} onClick={onExternalSubmitClick}>
              Submit External
            </WsButton>
            {onSubmissionRejection && (
              <WsButton
                variant="text"
                startIcon={<BlockIcon />}
                onClick={handleSubmissionRejection}
              >
                Reject Internally
              </WsButton>
            )}
          </Box>
        </Popover>
      )}
      <WsDialog openDialog={internalDialogOpen} size={WsDialogSize.SMALL}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          bgcolor={WsColors.BorderGrey}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK} bold>
            Submit Internally
          </WsTypography>
          <IconButton onClick={() => setInternalDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box p={2} gap={1}>
          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK} bold>
            {`Add email to assign to other's queue`}
          </WsTypography>
          <Box mt={2}>
            <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
              Email Address
            </WsTypography>
            <RecruiterSuggestions
              name="recruiters"
              multiple={false}
              placeholder="Type to add email"
              requireRecruiter={true}
              value={recruiter?.email}
            />
          </Box>
          <DialogActions sx={{ pt: 2 }}>
            {!onSubmissionRejection && (
              <WsButton onClick={handleEmailPreview}>Email preview</WsButton>
            )}
            <WsButton onClick={handleInternalSubmit}>Submit</WsButton>
          </DialogActions>
        </Box>
      </WsDialog>
      <WsDialog openDialog={externalDialogOpen} size={WsDialogSize.MEDIUM}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          bgcolor={WsColors.BorderGrey}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK} bold>
            Submit Externally
          </WsTypography>
          <IconButton onClick={() => setExternalDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Grid container p={2}>
          <Grid item container p={2}>
            {submissionData && (
              <SubmissionPreview
                submissionData={submissionData}
                isExternalEligible={isAuthorizedViewClientListingAll}
              />
            )}
          </Grid>
          <Grid item container direction="row" spacing={2} mt={2} gap={2} justifyContent="flex-end">
            <WsButton variant="outlined" onClick={() => handleExternalSubmit(false)} size="large">
              Dismiss
            </WsButton>
            <WsButton
              onClick={() => handleExternalSubmit(true)}
              size="large"
              disabled={!isAuthorizedViewClientListingAll}
            >
              Proceed
            </WsButton>
          </Grid>
        </Grid>
      </WsDialog>
      <WsDialog openDialog={openEmailPreview} size={WsDialogSize.MEDIUM}>
        <DialogTitle sx={{ p: 2 }}>
          <Grid container alignItems="center">
            <Grid item xs>
              <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                Email preview
              </WsTypography>
            </Grid>
            <Grid item>
              <IconButton onClick={() => setOpenEmailPreview(false)} size="small">
                <CloseIcon sx={{ color: WsColors.GreyBlack }} />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <WsEditor editor={editor} disable={true} isLoaded={isEmailDataLoaded} />
        </DialogContent>
      </WsDialog>
    </>
  );
};

export default SubmitOptions;
